import React, { FC, useEffect, useCallback } from "react";
import { format } from "lib/globalize-util";
import makeStyles from "@material-ui/core/styles/makeStyles";
import MediaDialog from "components/dialog/media-dialog";
import { NotificationError, useNotificationState } from "components/notification-banners";
import classNames from "classnames";
import theme from "styles/theme";
import { FormatMessage } from "react-globalize";
import Grid from "@material-ui/core/Grid";
import FilesIcon from "components/icons/files";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import Reminders from "components/product-media/components/reminders";
import { DropzoneUploader, withMediaStyles } from "components/upload/dropzone-uploader";
import { IStore } from "reducers";
import { connect, Dispatch } from "react-redux";
import { FileWithPreview } from "react-dropzone";
import { uploadFiles, resetImport } from "actions/color-swaches";
import _ from "lodash";
import { UploadErrors } from "components/admin/color-swatches/upload-errors";
import { DragMessage } from "components/upload/components";

const MAX_FILES = 50;
const MAX_SIZE = 70; // kB

interface IReduxProps {
    errorList: string[];
    isUploading: boolean;
}

interface IDispatchProps {
    resetImport(): void;
    uploadImages(file: FileWithPreview): void;
}

interface IProps {
    open: boolean;
    onClose: () => void;
}

const UploadImagesDialog: FC<IProps & IReduxProps & IDispatchProps> = (props) => {
    const { open, onClose, uploadImages, resetImport } = props;
    const { isUploading, errorList } = props;

    const [uploadError, handleError, hideError] = useNotificationState(
        format(
            "There was an error uploading your data. Review the errors below, make the necessary changes and try again."
        )
    );

    useEffect(() => {
        if (open) {
            resetImport();
            hideError();
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isUploading) {
            hideError();
        } else if (!_.isEmpty(errorList)) {
            handleError();
        }
    }, [errorList, isUploading]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDrop = useCallback(
        (files: File[]) => {
            if (!_.isEmpty(files)) {
                uploadImages(files);
            }
        },
        [uploadImages]
    );

    const classes = swatchesBulkUploadStyles({});
    const dropzoneStyles = withMediaStyles();
    return (
        <MediaDialog
            open={open}
            classes={{ paper: classes.paper }}
            title={format("Bulk Upload Swatch Images")}
            onClose={onClose}
        >
            <div className={classes.contentTitle}>{format("Upload new swatches or update existing swatches")}</div>
            <NotificationError classes={{ root: classes.error }} message={uploadError} onClose={hideError} />
            <Grid container={true} wrap="wrap" justify="space-around" spacing={2}>
                <Grid className={classes.margin} item={true}>
                    <DropzoneUploader
                        multiple={true}
                        loading={isUploading}
                        accept="image/jpeg,image/jpg"
                        maxFiles={MAX_FILES}
                        maxSize={MAX_SIZE * 1024}
                        minWidth={300}
                        maxWidth={300}
                        minHeight={300}
                        maxHeight={300}
                        onDrop={handleDrop}
                        onUploadError={handleError}
                        {...dropzoneStyles}
                    >
                        {errorList && _.isEmpty(errorList) ? (
                            <DoneAllIcon className={classNames(classes.icon, classes.iconSuccess)} />
                        ) : (
                            <FilesIcon className={classes.icon} />
                        )}
                        <DragMessage path={"Drag your image files here |br| or |span|click here|span| to select"} />
                    </DropzoneUploader>
                </Grid>
                <Grid className={classes.margin} item={true} xs={true}>
                    <Reminders title={format("Image specifications & upload tips:")}>
                        <li>{format("JPG only")}</li>
                        <li>{format("Upload up to |n| files at one time", { n: MAX_FILES })}</li>
                        <li>{format("Image size: |w| x |h| required", { w: "300px", h: "300px" })}</li>
                        <li>{format("File size |size| max", { size: `${MAX_SIZE}kb` })}</li>
                        <li>
                            {format("Make sure image files are named correctly for each color!")}
                            <Reminders className={classes.reminderSecondary}>
                                <li>
                                    {format(
                                        "Names with special characters: Color Name: Black/White -> File Name: blackwhite.jpg (remove any slashes or other special characters)"
                                    )}
                                </li>
                            </Reminders>
                        </li>
                        <li>
                            <FormatMessage
                                path="For the truest possible colors, make sure images have been 'saved for web' in the RGB format (|a|Learn more|a| about saving optimized images for the web)"
                                elements={{
                                    a: (
                                        <a
                                            className={classes.link}
                                            target="_blank"
                                            href="https://www.web-development-blog.com/best-methods-to-optimize-jpeg-images-for-websites"
                                        />
                                    ),
                                }}
                            />
                        </li>
                    </Reminders>
                </Grid>
                <UploadErrors errorList={errorList} />
            </Grid>
        </MediaDialog>
    );
};

export const swatchesBulkUploadStyles = makeStyles({
    paper: {
        maxWidth: "960px",
    },
    contentTitle: {
        fontSize: 14,
        padding: "0 10px",
    },
    error: {
        margin: 10,
    },
    reminderSecondary: {
        "& li:before": {
            color: theme.palette.grey,
        },
    },
    icon: {
        width: 160,
        height: 160,
        padding: 30,
        color: theme.palette.grey,
    },
    iconSuccess: {
        color: theme.palette.accent1Color,
    },
    margin: {
        margin: 20,
        ["@media(max-width: 600px)"]: {
            margin: 10,
        },
    },
    link: {
        textDecoration: "underline",
        color: theme.palette.accent1Color,
        padding: 0,
    },
    strong: {
        fontWeight: "bold",
    },
});

const mapStateToProps = (store: IStore): IReduxProps => ({
    isUploading: store.colorSwatches.import.isUploading,
    errorList: store.colorSwatches.import.payload?.errorList,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>): IDispatchProps => ({
    resetImport: () => dispatch(resetImport()),
    uploadImages: (files: FileWithPreview[]) => dispatch(uploadFiles(files)),
});

export default connect<IReduxProps, IDispatchProps, IProps>(mapStateToProps, mapDispatchToProps)(UploadImagesDialog);
